import { ApiResponse, GlobalStore, GridStore, UserStore } from '@roc/feature-app-core';
import { GENERIC_ERROR_MESSAGE } from '@roc/feature-utils';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { StatusDashboardService } from '../services/statusDashboardService';
import { CHANNEL_LABELS, Channel, ROLE_NOTES, Role, SKIP_REASON_LABELS, View } from '../utils/statusDashboardConstants';
import {
  ApiDate,
  AudienceView,
  EmailCampaignDetailResponse,
  EmailCampaignResponse,
  EmailOverviewResponse,
  EmailRecipientHistoryResponse,
  EmailRecipientResponse,
  CampaignView,
  FailureReason,
  FunnelStage,
  HistoryView,
  RecipientProfileView,
  RecipientStat,
  RecipientView,
} from '../utils/statusDashboardTypes';

const ALL_BRANDS = 'All brands';
// What the backend expects for every sender; in the store no sender chosen means every sender
const ALL_SENDERS = 'all';
const CORPORATE_SENDER_OPTIONS = [
  { value: 'hello@facolending.com', label: 'Corporate (FaCo)' },
  { value: 'hello@civicfs.com', label: 'Corporate (Civic)' },
];
export const PAGE_SIZE = 10;

// GridStore keeps its own page size of 200, so the page size is set on its meta
const createGrid = (fetchData: () => Promise<ApiResponse>) => {
  const grid = new GridStore(fetchData, null, PAGE_SIZE);
  grid.gridData.meta.pageSize = PAGE_SIZE;
  return grid;
};

// Columns the backend can sort the campaigns by
export type CampaignSortColumn = 'requested' | 'sent' | 'deliveredRate' | 'openRate' | 'clickRate' | 'optOut';
export type RecipientSortColumn = 'name' | 'address' | 'sender' | 'audience' | 'opened' | 'status' | 'skipReason';
export type SortDir = 'ASC' | 'DESC';

// Clicking a column sorts it in its first direction, then the other way, then back to the default order of the table
const getNextSort = (grid: GridStore, column: string, firstDir: SortDir) => {
  const { sortBy, sortDir } = grid.gridData.meta;
  const otherDir: SortDir = firstDir === 'ASC' ? 'DESC' : 'ASC';
  if (sortBy !== column) return { sortBy: column, sortDir: firstDir };
  if (sortDir === firstDir) return { sortBy: column, sortDir: otherDir };
  return { sortBy: undefined, sortDir: undefined };
};

// Column filters of a table, as the leads dashboard keeps them in its GridStore: { [column]: text }
export type ColumnFilters = Record<string, string>;

export const getColumnFilters = (grid: GridStore): ColumnFilters => grid.gridData.meta.filters ?? {};

const setColumnFilter = (grid: GridStore, column: string, value: string) => {
  const filters = { ...getColumnFilters(grid) };
  const text = value.trim();
  if (filters[column] === text || (!text && !filters[column])) return;
  if (text) {
    filters[column] = text;
  } else {
    delete filters[column];
  }
  grid.setPageNumber(1, false);
  grid.setFiltersParam({ filters, dropdownFilters: undefined }, true);
};


const formatPct = (part: number, total: number) => (total ? `${((part / total) * 100).toFixed(1)}%` : '—');

// roc-services sends LocalDateTime as [year, month, day, hour, minute, second, nanos]
const parseApiDate = (value: ApiDate): Date => {
  if (Array.isArray(value)) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    return new Date(year, month - 1, day, hour, minute, second);
  }
  return new Date(value.replace(' ', 'T'));
};

const formatDate = (value: ApiDate) => {
  if (!value) return '—';
  const date = parseApiDate(value);
  return isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const toIsoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// First day included by each option of the Range filter
export const getRangeStartDate = (range: string, today = new Date()) => {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  switch (range) {
    case 'Last 7 days':
      start.setDate(start.getDate() - 7);
      break;
    case 'Quarter to date':
      start.setMonth(Math.floor(start.getMonth() / 3) * 3, 1);
      break;
    case 'Year to date':
      start.setMonth(0, 1);
      break;
    default:
      start.setDate(start.getDate() - 30);
  }
  return toIsoDate(start);
};

const toCampaignView = (c: EmailCampaignResponse): CampaignView => ({
  key: c.groupKey,
  groupKey: c.groupKey,
  name: c.subject || 'No subject',
  date: formatDate(c.lastSentDate) + (c.sendDays > 1 ? ` · sent on ${c.sendDays} days` : ''),
  segment: c.audiences.join(', ') || '—',
  audiences: c.audiences,
  senders: c.ownerEmails ?? [],
  requested: c.requestedCount,
  skipped: c.skippedCount,
  sent: c.sentCount,
  deliveredRate: formatPct(c.deliveredCount, c.sentCount),
  openRate: formatPct(c.openedCount, c.sentCount),
  clickRate: formatPct(c.clickedCount, c.sentCount),
  optOut: c.unsubscribedCount,
});

const toRecipientView = (r: EmailRecipientResponse): RecipientView => ({
  key: r.recipientKey,
  name: r.recipientName || r.address || '—',
  address: r.address || '—',
  segment: r.audience || '—',
  sender: r.ownerEmail?.toLowerCase() ?? '',
  openCount: r.openCount ?? null,
  status: r.status,
  skipReason: r.status === 'SKIPPED' ? r.detail || '—' : '—',
  leadId: r.leadId,
  contactId: r.contactId,
});

export class StatusDashboardStore {
  public globalStore: GlobalStore;
  private userStore: UserStore;
  private statusDashboardService: StatusDashboardService;
  public view: View;
  public role: Role;
  public channel: Channel;
  public range: string;
  public brand: string;
  // Senders whose emails are shown in each view, when the user can choose them (see canChooseSender); empty for every one
  public outreachOriginators: string[];
  public marketingSenders: string[];
  public originatorOptions: { value: string; label: string }[];
  public selectedCampaign: CampaignView;
  public selectedRecipient: RecipientView;
  public isCampaignOpen: boolean;
  public isRecipientOpen: boolean;
  public overview: EmailOverviewResponse;
  public campaignDetail: EmailCampaignDetailResponse;
  public recipientHistory: EmailRecipientHistoryResponse;
  // Campaigns of the overview and recipients of the opened campaign, PAGE_SIZE at a time
  public campaignsGrid: GridStore;
  public recipientsGrid: GridStore;

  constructor(globalStore: GlobalStore, userStore: UserStore) {
    this.globalStore = globalStore;
    this.userStore = userStore;
    this.statusDashboardService = new StatusDashboardService();
    this.setDefaults();
    makeObservable(this, {
      view: observable,
      role: observable,
      channel: observable,
      range: observable,
      brand: observable,
      outreachOriginators: observable.ref,
      marketingSenders: observable.ref,
      originatorOptions: observable.ref,
      setSenders: action,
      loadOriginators: flow,
      canChooseSender: computed,
      senders: computed,
      allSendersLabel: computed,
      showSenders: computed,
      skipReasonOptions: computed,
      campaignSenderOptions: computed,
      senderLabel: computed,
      senderOptions: computed,
      selectedCampaign: observable.ref,
      selectedRecipient: observable.ref,
      isCampaignOpen: observable,
      isRecipientOpen: observable,
      setCampaignFilter: action,
      setCampaignSort: action,
      setRecipientSort: action,
      setRecipientFilter: action,
      overview: observable.ref,
      campaignDetail: observable.ref,
      recipientHistory: observable.ref,
      campaignsGrid: observable.ref,
      recipientsGrid: observable.ref,
      setView: action,
      setRole: action,
      setChannel: action,
      setRange: action,
      setBrand: action,
      openCampaign: action,
      openRecipient: action,
      closeDetails: action,
      reset: action,
      loadOverview: flow,
      loadCampaign: flow,
      loadRecipientHistory: flow,
      isMarketing: computed,
      availableViews: computed,
      channelData: computed,
      campaigns: computed,
      sentTotal: computed,
      roleNote: computed,
      audiences: computed,
      campaignFunnel: computed,
      campaignFailedTotal: computed,
      campaignFailures: computed,
      recipients: computed,
      recipientProfile: computed,
      recipientStats: computed,
      history: computed,
    });
  }

  setDefaults() {
    this.view = 'overview';
    this.role = 'marketing';
    this.channel = 'email';
    this.range = 'Last 30 days';
    this.brand = ALL_BRANDS;
    this.outreachOriginators = this.currentUserEmail ? [this.currentUserEmail] : [];
    this.marketingSenders = [];
    this.originatorOptions = [];
    this.selectedCampaign = null;
    this.selectedRecipient = null;
    this.isCampaignOpen = false;
    this.isRecipientOpen = false;
    this.overview = null;
    this.campaignDetail = null;
    this.recipientHistory = null;
    this.campaignsGrid = createGrid(() => this.fetchCampaigns());
    this.recipientsGrid = createGrid(() => this.fetchRecipients());
  }

  reset() {
    this.setDefaults();
  }

  setView(view: View) {
    this.view = view;
    // Going back hides the tabs that come after the selected one
    if (view === 'overview') {
      this.isCampaignOpen = false;
      this.isRecipientOpen = false;
    } else if (view === 'campaign') {
      this.isRecipientOpen = false;
    }
  }

  setRole(role: Role) {
    this.role = role;
    this.closeDetails();
    if (role === 'marketing') {
      // Marketing blasts are email only
      this.channel = 'email';
    } else {
      this.brand = ALL_BRANDS;
    }
    this.loadOverview();
  }

  setChannel(channel: Channel) {
    // Email only until SMS bulk sends are tracked
    if (channel === 'sms') return;
    this.channel = channel;
    this.closeDetails();
    this.loadOverview();
  }

  setRange(range: string) {
    this.range = range;
    this.closeDetails();
    this.loadOverview();
  }

  setBrand(brand: string) {
    this.brand = brand;
    this.closeDetails();
    this.loadOverview();
  }

  // column is "name" (the campaign subject)
  setCampaignFilter(column: string, value: string) {
    this.closeDetails();
    setColumnFilter(this.campaignsGrid, column, value);
  }

  // Clicking a column sorts it from highest to lowest, then lowest to highest, then back to the most recent campaigns
  setCampaignSort(column: CampaignSortColumn) {
    this.closeDetails();
    this.campaignsGrid.setPageNumber(1, false);
    this.campaignsGrid.setSortParams(getNextSort(this.campaignsGrid, column, 'DESC'), true);
  }

  // Text columns sort from A to Z first and Opened from the most opens; the default order is the sent emails, newest first
  setRecipientSort(column: RecipientSortColumn) {
    this.recipientsGrid.setPageNumber(1, false);
    this.recipientsGrid.setSortParams(getNextSort(this.recipientsGrid, column, column === 'opened' ? 'DESC' : 'ASC'), true);
  }

  // column is "name" or "address" (email)
  setRecipientFilter(column: string, value: string) {
    setColumnFilter(this.recipientsGrid, column, value);
  }

  openCampaign(campaign: CampaignView) {
    this.selectedCampaign = campaign;
    this.isCampaignOpen = true;
    this.view = 'campaign';
    this.loadCampaign();
  }

  openRecipient(recipient: RecipientView) {
    this.selectedRecipient = recipient;
    this.isRecipientOpen = true;
    this.view = 'recipient';
    this.loadRecipientHistory();
  }

  // Filters change the campaign list, so the opened campaign and recipient no longer apply
  closeDetails() {
    this.selectedCampaign = null;
    this.selectedRecipient = null;
    this.isCampaignOpen = false;
    this.isRecipientOpen = false;
    this.view = 'overview';
  }

  // ---- API ----

  // Brand only filters marketing blasts
  private get brandFilter() {
    return this.isMarketing && this.brand !== ALL_BRANDS ? this.brand : undefined;
  }

  // Senders chosen in the current view, comma separated; the backend shows everyone else their own emails
  private get senderFilter() {
    if (!this.canChooseSender) return undefined;
    return this.senders.length > 0 ? this.senders.join(',') : ALL_SENDERS;
  }

  // Emails are kept in lowercase, so the user's own email matches its option
  private get currentUserEmail() {
    return this.userStore?.userInformation?.username?.toLowerCase();
  }

  // Back office leads choose the originators of the outreach; marketing and back office leads the senders of marketing blasts
  get canChooseSender() {
    if (!this.isMarketing) return !!this.userStore?.isBackofficeLead;
    return !!this.userStore?.isBackofficeLead || !!this.userStore?.isMarketingUser;
  }

  get senders() {
    return this.isMarketing ? this.marketingSenders : this.outreachOriginators;
  }

  get allSendersLabel() {
    return this.isMarketing ? 'All senders' : 'All originators';
  }

  // The tables say who sent each campaign and email when more than one sender is shown
  get showSenders() {
    return this.canChooseSender && this.senders.length !== 1;
  }

  get senderLabel() {
    return this.isMarketing ? 'Sender' : 'Originator';
  }

  // Senders the campaigns table can be narrowed to: the ones chosen above, or every one when all are shown
  get campaignSenderOptions() {
    if (this.senders.length === 0) return this.senderOptions;
    return this.senders.map(sender => ({ value: sender, label: this.senderName(sender) }));
  }

  // Skip reasons of the open campaign, for the Skip reason filter of its recipients
  get skipReasonOptions() {
    return (this.campaignDetail?.skipReasons ?? []).map(r => ({
      value: r.reason,
      label: SKIP_REASON_LABELS[r.reason] ?? r.reason,
    }));
  }

  // Name of a sender as the select shows it, the email when it is not an option
  senderName(email: string) {
    if (!email) return '—';
    return this.senderOptions.find(option => option.value === email.toLowerCase())?.label ?? email;
  }

  // Marketing blasts can also come from the corporate addresses, which are not originators
  get senderOptions() {
    return this.isMarketing ? [...CORPORATE_SENDER_OPTIONS, ...this.originatorOptions] : this.originatorOptions;
  }

  setSenders(senders: string[]) {
    if (this.isMarketing) {
      this.marketingSenders = senders;
    } else {
      this.outreachOriginators = senders;
    }
    // senders ticked in the Sender column may no longer be shown
    const { sender, ...filters } = getColumnFilters(this.campaignsGrid);
    if (sender) this.campaignsGrid.setFiltersParam({ filters, dropdownFilters: undefined }, false);
    this.closeDetails();
    this.loadOverview();
  }

  *loadOriginators() {
    if (!this.userStore?.isBackofficeLead && !this.userStore?.isMarketingUser) return;
    // The user information can arrive after the store was created
    if (this.outreachOriginators.length === 0 && this.currentUserEmail) {
      this.outreachOriginators = [this.currentUserEmail];
    }
    try {
      const response = yield this.statusDashboardService.getOriginators();
      const owners: { email: string; originatorName: string }[] = response.data.data ?? [];
      const options = owners
        .filter(owner => owner.email)
        .map(owner => ({ value: owner.email.toLowerCase(), label: owner.originatorName?.trim() || owner.email }))
        .sort((a, b) => a.label.localeCompare(b.label));
      // Their own outreach is selected by default, so they are always an option
      const currentUser = this.currentUserEmail;
      if (currentUser && !options.some(o => o.value === currentUser)) {
        options.unshift({ value: currentUser, label: this.userStore.userInformation.fullName || currentUser });
      }
      this.originatorOptions = options;
    } catch (e) {
      this.showError();
    }
  }

  private showError() {
    this.globalStore.notificationStore.showErrorNotification({ message: GENERIC_ERROR_MESSAGE });
  }

  // Totals and the first page of campaigns
  *loadOverview() {
    this.campaignsGrid.setPageNumber(1);
    try {
      const response = yield this.statusDashboardService.getOverview(
        this.role,
        getRangeStartDate(this.range),
        this.brandFilter,
        this.senderFilter
      );
      this.overview = response.data.data;
    } catch (e) {
      this.showError();
    }
  }

  private fetchCampaigns = async () => {
    const { pageNumber, pageSize, sortBy, sortDir } = this.campaignsGrid.gridData.meta;
    const filters = getColumnFilters(this.campaignsGrid);
    try {
      return await this.statusDashboardService.getCampaigns(
        this.role,
        getRangeStartDate(this.range),
        this.brandFilter,
        this.senderFilter,
        filters,
        sortBy,
        sortDir,
        pageNumber,
        pageSize
      );
    } catch (e) {
      this.showError();
    }
  };

  private fetchRecipients = async () => {
    const { pageNumber, pageSize, sortBy, sortDir } = this.recipientsGrid.gridData.meta;
    const filters = getColumnFilters(this.recipientsGrid);
    if (!this.selectedCampaign) return null;
    try {
      return await this.statusDashboardService.getCampaignRecipients(
        this.role,
        this.selectedCampaign.groupKey,
        getRangeStartDate(this.range),
        this.brandFilter,
        this.senderFilter,
        filters,
        sortBy,
        sortDir,
        pageNumber,
        pageSize
      );
    } catch (e) {
      this.showError();
    }
  };

  // Counts of the campaign and the first page of its recipients
  *loadCampaign() {
    if (!this.selectedCampaign) return;
    this.campaignDetail = null;
    // Filters and sort of the previous campaign's recipients do not apply to this one
    this.recipientsGrid.gridData.meta.filters = undefined;
    this.recipientsGrid.setSortParams({ sortBy: undefined, sortDir: undefined }, false);
    this.recipientsGrid.setPageNumber(1);
    try {
      const response = yield this.statusDashboardService.getCampaign(
        this.role,
        this.selectedCampaign.groupKey,
        getRangeStartDate(this.range),
        this.brandFilter,
        this.senderFilter
      );
      this.campaignDetail = response.data.data;
    } catch (e) {
      this.showError();
    }
  }

  *loadRecipientHistory() {
    if (!this.selectedRecipient) return;
    const recipient = this.selectedRecipient;
    this.recipientHistory = null;
    try {
      const response = yield this.statusDashboardService.getRecipientHistory(
        recipient.leadId,
        recipient.contactId,
        recipient.leadId || recipient.contactId ? undefined : recipient.address
      );
      this.recipientHistory = response.data.data;
    } catch (e) {
      this.showError();
    }
  }

  // ---- Views ----

  get availableViews(): View[] {
    return [
      'overview',
      ...(this.isCampaignOpen ? ['campaign' as View] : []),
      ...(this.isRecipientOpen ? ['recipient' as View] : []),
    ];
  }

  get isMarketing() {
    return this.role === 'marketing';
  }

  // Column labels of the selected channel
  get channelData() {
    return CHANNEL_LABELS[this.channel];
  }

  // Campaigns of the current page
  get campaigns(): CampaignView[] {
    const rows = this.campaignsGrid.gridData.data.rows ?? [];
    return rows.map(toCampaignView);
  }

  get sentTotal() {
    return this.overview?.sentCount ?? 0;
  }

  get roleNote() {
    const brandNote = this.brand !== ALL_BRANDS ? this.brand : 'all brands';
    return `${ROLE_NOTES[this.role]} · ${brandNote}`;
  }

  get audiences(): AudienceView[] {
    const audiences = this.overview?.audiences ?? [];
    const total = audiences.reduce((sum, a) => sum + a.sentCount, 0);
    return audiences.map(a => ({
      name: a.audience || 'Other',
      size: a.sentCount,
      share: total ? Math.round((a.sentCount / total) * 100) : 0,
      engagement: formatPct(a.openedCount, a.sentCount),
      optOut: formatPct(a.unsubscribedCount, a.sentCount),
    }));
  }

  get campaignFunnel(): FunnelStage[] {
    const c = this.selectedCampaign;
    if (!c) return [];
    const detail = this.campaignDetail?.campaign;
    const stages = [
      { label: 'Requested', count: detail?.requestedCount ?? c.requested },
      { label: 'Sent', count: detail?.sentCount ?? c.sent },
      { label: 'Delivered', count: detail?.deliveredCount ?? 0 },
      { label: 'Opened', count: detail?.openedCount ?? 0 },
      { label: 'Clicked', count: detail?.clickedCount ?? 0 },
    ];
    const requested = stages[0].count;
    return stages.map(s => ({ ...s, pct: requested ? (s.count / requested) * 100 : 0 }));
  }

  get campaignFailedTotal() {
    const detail = this.campaignDetail?.campaign;
    return detail ? detail.bouncedCount + detail.droppedCount : 0;
  }

  get campaignFailures(): FailureReason[] {
    const reasons = this.campaignDetail?.failureReasons ?? [];
    const max = Math.max(1, ...reasons.map(r => r.count));
    return reasons.map(r => ({ reason: r.reason, count: r.count, pct: (r.count / max) * 100 }));
  }

  // Recipients of the current page
  get recipients(): RecipientView[] {
    return (this.recipientsGrid.gridData.data.rows ?? []).map(toRecipientView);
  }

  get recipientProfile(): RecipientProfileView {
    const recipient = this.selectedRecipient;
    const profile = this.recipientHistory?.profile;
    const name = profile ? [profile.firstName, profile.lastName].filter(Boolean).join(' ') : '';
    return {
      name: name || recipient?.name || '—',
      segment: recipient?.segment || '—',
      address: profile?.email || recipient?.address || '—',
      phone: profile?.phone || '—',
    };
  }

  get recipientStats(): RecipientStat[] {
    const history = this.recipientHistory;
    const rate = (value: number) => (value === null || value === undefined ? '—' : `${value.toFixed(1)}%`);
    return [
      { label: 'Sends received', value: String(history?.sendsReceived ?? 0) },
      { label: 'Open rate', value: rate(history?.openRate) },
      { label: 'Click rate', value: rate(history?.clickRate) },
      { label: 'Last engaged', value: formatDate(history?.lastEngagedDate) },
    ];
  }

  get history(): HistoryView[] {
    return (this.recipientHistory?.sends ?? []).map(h => ({
      key: h.recipientKey,
      date: formatDate(h.sentDate),
      channel: h.channel === 'EMAIL' ? 'Email' : h.channel,
      campaign: h.subject || 'No subject',
      detail: h.detail,
      status: h.status,
    }));
  }
}
